import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import intl from 'react-intl-universal';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import LinearProgress from '@mui/material/LinearProgress';

import { alertAction } from '../../thunks/Alerts';
import {
  selectActiveBoard,
  selectIsLoadingActiveBoard,
  selectIsLoadingRoomMembers,
  selectRoomMembersRequested,
} from '../../ducks/Boards';
import { actions as communityActions } from '../../ducks/Communities';
import { actions as roomActions } from '../../ducks/Rooms';
import * as boardsActions from '../../thunks/Boards';
import {
  LIST_BOARD_VIEW_MODE,
  LIST_DELIVERY_VIEW_MODE,
  LIST_MILESTONE_VIEW_MODE,
  TILE_BOARD_VIEW_MODE,
  WEEK_BOARD_VIEW_MODE,
} from '../../utils/ViewModeUtil';

import Error from '../../common/Error';
import ListBoardContainer from './ListBoardContainer';
import TileBoardContainer from './TileBoardContainer';
import WeekBoardContainer from './WeekBoardContainer';
import ListMilestoneContainer from './ListMilestoneContainer';
import ListDeliveryContainer from './ListDeliveryContainer';

const mapStateToProps = () =>
  createStructuredSelector({
    activeBoard: selectActiveBoard(),
    isLoading: selectIsLoadingActiveBoard(),
    isLoadingRoomMembers: selectIsLoadingRoomMembers(),
    roomMembersRequested: selectRoomMembersRequested(),
  });

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      ...boardsActions,
      setActiveRoom: roomActions.setActiveRoom,
      setActiveCommunity: communityActions.setActiveCommunity,
    },
    dispatch,
  ),
});

const BoardContainer = ({
  activeBoard,
  isLoading,
  isLoadingRoomMembers,
  roomMembersRequested,
  actions: { fetchActiveBoard, fetchRoomMembers, setActiveCommunity, setActiveRoom },
}: any) => {
  const { communityId, roomId, boardId } = useParams();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (roomMembersRequested.communityId !== communityId || roomMembersRequested.roomId !== roomId) {
      fetchRoomMembers({ communityId, roomId, boardId });
    }

    if (activeBoard === null || activeBoard.id !== boardId) {
      fetchActiveBoard({ communityId, roomId, boardId }).then((action: any) => {
        const workflowConfig = action.payload.activeBoard.workflow_config;

        setActiveCommunity({ communityId: workflowConfig.community_id });
        setActiveRoom({ roomId: workflowConfig.room_id });

        return alertAction({ action, error: intl.get('board.fetch.error') });
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId, roomId, boardId]);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!activeBoard) {
    return <Error text={intl.get('board.not_found')} />;
  }

  if (
    activeBoard?.id !== boardId ||
    isLoadingRoomMembers ||
    roomMembersRequested.communityId !== communityId ||
    roomMembersRequested.roomId !== roomId
  ) {
    return <LinearProgress />;
  }

  const view = searchParams.get('view');

  // Default view is tile board
  if (!view || view === TILE_BOARD_VIEW_MODE) {
    return <TileBoardContainer />;
  }

  if (view === WEEK_BOARD_VIEW_MODE) {
    return <WeekBoardContainer />;
  }

  if (view === LIST_BOARD_VIEW_MODE) {
    return <ListBoardContainer />;
  }

  if (view === LIST_MILESTONE_VIEW_MODE) {
    return <ListMilestoneContainer />;
  }

  if (view === LIST_DELIVERY_VIEW_MODE) {
    return <ListDeliveryContainer />;
  }

  return (
    <Error
      text={intl.get('board.views.not_found', {
        mode: view,
        available_modes: [TILE_BOARD_VIEW_MODE, LIST_BOARD_VIEW_MODE],
      })}
    />
  );
};

const ConnectedBoardContainer = connect(mapStateToProps, mapDispatchToProps)(BoardContainer);

export default ConnectedBoardContainer;
